<template>
  <div class="flex min-h-full grow flex-col justify-between">
    <div>
      <NavBarMobileSubmenu
        :is-open="isSubmenuOpen"
        :title="submenu.title"
        :links="submenu.links"
        :is-logged-in="isLoggedIn"
        @close-submenu="closeSubmenu"
        @close-whole-menu="closeWholeMenu"
      />
      <div v-if="shouldShowMainMenu" class="mt-6 flex flex-col space-y-4">
        <template v-for="navigationLink in mainLinks" :key="navigationLink.label">
          <NavBarMobileMenuItem
            :link="navigationLink"
            :is-logged-in="isLoggedIn"
            @open-submenu="openSubmenu($event)"
            @close="emit('close')"
          >
            <template #howItWorksBtn>
              <button class="inline-flex items-center text-burnt-sienna" @click="openModal">
                How recipe kits work
              </button>
            </template>
          </NavBarMobileMenuItem>
        </template>
      </div>
    </div>

    <div v-if="shouldShowMainMenu">
      <router-link
        v-if="!isLoggedIn"
        to="/social-login"
        class="flex w-full items-center justify-center bg-primary-green px-6 py-4 text-white hover:bg-cavolo-green"
        @click="$emit('close')"
      >
        Log in
      </router-link>

      <router-link
        v-if="!isLoggedIn"
        to="/social-login"
        class="mt-6 flex w-full items-center justify-center border border-sienna-muted bg-light-linen px-6 py-4 text-burnt-sienna hover:bg-linen"
        @click="$emit('close')"
      >
        Sign up
      </router-link>

      <button
        v-else
        class="flex w-full items-center justify-center border border-sienna-muted bg-light-linen px-6 py-4 text-burnt-sienna hover:bg-linen"
        @click="$emit('logout')"
      >
        Log out
      </button>
    </div>
  </div>
  <NavBarTakeawayUnavailable
    v-model:is-open="showTakeawayUnavailableModal"
    @click="closeWholeMenu"
  />
</template>

<script lang="ts" setup>
  import { experienceLinks, giftMenuLinks, storesLinks } from '@/constants/links';
  import { NavigationLink, SubmenuGroup } from '@/types/navigation';
  import { trackCustomEvent } from '@/utils/analytics';

  interface Props {
    isLoggedIn?: boolean;
    showMyAccountLinks?: boolean;
  }

  interface Emits {
    (e: 'close'): void;
    (e: 'openModal'): void;
    (e: 'logout'): void;
  }

  withDefaults(defineProps<Props>(), { isLoggedIn: false });
  const emit = defineEmits<Emits>();

  const submenu = reactive<SubmenuGroup>({
    title: { label: '', path: '' },
    links: [],
  });
  const shouldShowMainMenu = ref(true);
  const showTakeawayUnavailableModal = ref(false);

  const myAccountLinks = computed(() => {
    return [
      {
        label: 'Orders',
        name: 'order-history',
      },
      {
        label: 'Pasta plan',
        name: 'upcoming-orders',
      },
      {
        label: 'Plan settings',
        name: 'pasta-plan',
      },
      {
        label: 'Rewards',
        name: 'rewards',
      },
      {
        label: 'Profile',
        name: 'details',
      },
      {
        label: 'Help',
        name: 'support',
      },
    ];
  });

  const mainLinks = computed((): NavigationLink[] => {
    const links = [
      {
        label: 'Restaurant Menus',
        name: '',
        children: [
          {
            label: 'Restaurant menu',
            path: 'https://cdn.shopify.com/s/files/1/1725/5449/files/PE_TableMenu_all_LW.pdf?v=1761056106',
          },
          {
            label: 'Harrods menu',
            path: 'https://cdn.shopify.com/s/files/1/1725/5449/files/APR25_Booklet_inner_Harrods_menu_2025_2_1.pdf?v=1761056106',
          },
        ],
      },
      {
        label: 'Book a table',
        name: 'restaurants',
        children: storesLinks,
      },
      { label: 'Delivery', name: 'pasta-now-search-address' },
      {
        label: 'Experiences',
        path: 'https://pastaevangelists.com/pages/events',
        children: experienceLinks,
      },
      { label: 'Catering', name: 'catering-search-address' },
      { label: 'Gifts', path: 'https://pastaevangelists.com/pages/gifts', children: giftMenuLinks },
      {
        label: ' My account',
        name: '',
        needsLogin: true,
        children: myAccountLinks.value,
      },
    ];

    return links;
  });

  const openModal = () => {
    trackCustomEvent('howItWorksClicked');
    emit('openModal');
  };

  const openSubmenu = (menu: SubmenuGroup) => {
    shouldShowMainMenu.value = false;
    submenu.title = menu.title;
    submenu.links = menu.links;
  };

  const isSubmenuOpen = computed(() => Boolean(submenu.links.length));

  const closeSubmenu = () => {
    submenu.links = [];
    submenu.title = { label: '', path: '' };
    shouldShowMainMenu.value = true;
  };

  const closeWholeMenu = () => {
    closeSubmenu();
    emit('close');
  };
</script>
